import { useMemo, useState, useEffect } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import "./CatalogIndex.css";

interface HasIdPosition {
  id: string | number;
  position?: number;
}

export interface ReorderMove {
  id: string | number;
  position: number;
}

export interface AdminListTableProps<T extends HasIdPosition> {
  columns: ColumnDef<T>[];
  data: T[];
  isLoading?: boolean;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  /** Render the per-row selection checkbox column. Defaults to true; pass
   * false for tables that have no batch action (e.g. a nested sub-list). */
  selectable?: boolean;
  draggable?: boolean;
  onReorder?: (moves: ReorderMove[]) => void;
  onEditHref?: (row: T) => string;
  onEdit?: (id: string | number) => void;
  editLinkDisabled?: boolean;
  editColumnId?: string;
  emptyMessage?: string;
  paginationEnabled?: boolean;
  pageSizeEnabled?: boolean;
  totalPages?: number;
  totalRows?: number;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
}

export const AdminListTable = <T extends HasIdPosition>({
  columns,
  data,
  isLoading,
  selectedIds,
  setSelectedIds,
  selectable = true,
  draggable = false,
  onReorder,
  onEditHref,
  onEdit,
  editLinkDisabled = false,
  editColumnId,
  emptyMessage = "No items yet.",
  paginationEnabled,
  pageSizeEnabled,
  totalPages,
  totalRows,
  currentPage,
  setCurrentPage,
  pageSize,
  onPageSizeChange,
}: AdminListTableProps<T>) => {
  // For draggable tables, sort by position then alphabetically by the first
  // column's value so duplicate positions render deterministically. Non-draggable
  // tables keep the server's order.
  const firstColumnAccessor = (
    columns[0] as { accessorKey?: string } | undefined
  )?.accessorKey;
  const sortedData = useMemo<T[]>(() => {
    if (!draggable) return data;
    const tiebreak = (a: T, b: T): number => {
      if (!firstColumnAccessor) return 0;
      const av = String(
        (a as Record<string, unknown>)[firstColumnAccessor] ?? ""
      );
      const bv = String(
        (b as Record<string, unknown>)[firstColumnAccessor] ?? ""
      );
      return av.localeCompare(bv);
    };
    return [...data].sort((a, b) => {
      const ap = a.position ?? 0;
      const bp = b.position ?? 0;
      if (ap !== bp) return ap - bp;
      return tiebreak(a, b);
    });
  }, [data, draggable, firstColumnAccessor]);

  const [localRows, setLocalRows] = useState<T[]>(sortedData);
  useEffect(() => setLocalRows(sortedData), [sortedData]);

  const showEditLink = !editLinkDisabled && (onEditHref || onEdit);

  const transformedColumns = useMemo<ColumnDef<T>[]>(() => {
    if (!showEditLink) return columns;
    const targetIndex = editColumnId
      ? Math.max(
          0,
          columns.findIndex(
            (c) => (c.id ?? (c as any).accessorKey) === editColumnId
          )
        )
      : 0;
    return columns.map((col, idx) => {
      if (idx !== targetIndex) return col;
      const originalCell = col.cell;
      return {
        ...col,
        cell: (ctx: any) => {
          const inner = originalCell
            ? flexRender(originalCell, ctx)
            : ctx.getValue?.() ?? "";
          if (onEditHref) {
            return (
              <a
                href={onEditHref(ctx.row.original)}
                className="adminListEditLink"
              >
                {inner}
                <FontAwesomeIcon
                  icon={faPencil}
                  className="adminListEditLink__icon"
                />
              </a>
            );
          }
          return (
            <a
              href="#"
              className="adminListEditLink"
              onClick={(e) => {
                e.preventDefault();
                onEdit?.(ctx.row.original.id);
              }}
            >
              {inner}
              <FontAwesomeIcon
                icon={faPencil}
                className="adminListEditLink__icon"
              />
            </a>
          );
        },
      };
    });
  }, [columns, showEditLink, onEditHref, onEdit, editColumnId]);

  const handleSetData = (updated: T[]) => {
    setLocalRows(updated);
    if (!onReorder) return;
    const moves: ReorderMove[] = [];
    for (const row of updated) {
      if (row.position == null) continue;
      const prev = data.find((d) => d.id === row.id);
      if (prev && prev.position !== row.position) {
        moves.push({ id: row.id, position: row.position });
      }
    }
    if (moves.length > 0) onReorder(moves);
  };

  if (isLoading) return <ScopeStackSpinner />;
  if (data.length === 0) {
    return <p className="text-muted mt-3 px-3">{emptyMessage}</p>;
  }

  return (
    <DataTable<T>
      data={localRows}
      setData={draggable && onReorder ? handleSetData : undefined}
      columns={transformedColumns}
      dragAndDrop={draggable}
      selectable={selectable}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      bordered
      striped
      hover
      classForTable="adminListTable"
      paginationEnabled={paginationEnabled}
      pageSizeEnabled={pageSizeEnabled}
      totalPages={totalPages}
      totalRows={totalRows}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
    />
  );
};
